<template>
  <div
    class="ui-loader"
    :class="[`ui-loader-${mode}`, { 'ui-loader-compact': size === 'sm' }]"
    role="status"
    aria-live="polite"
    :aria-busy="true"
  >
    <!-- Skeleton: placeholder rows shaped like a table / list -->
    <div v-if="variant === 'skeleton'" class="ui-skeleton" aria-hidden="true">
      <div class="ui-skeleton-head">
        <span class="ui-skel ui-skel-title"></span>
        <span class="ui-skel ui-skel-chip"></span>
      </div>
      <div v-for="n in rows" :key="n" class="ui-skeleton-row">
        <span class="ui-skel ui-skel-avatar"></span>
        <span class="ui-skel ui-skel-line" :style="{ width: 30 + ((n * 17) % 40) + '%' }"></span>
        <span class="ui-skel ui-skel-line ui-skel-short"></span>
        <span class="ui-skel ui-skel-pill"></span>
      </div>
    </div>

    <!-- Spinner: orbit ring + pulsing core -->
    <div v-else class="ui-spinner-wrap">
      <span class="ui-spinner" aria-hidden="true">
        <span class="ui-spinner-ring"></span>
        <span class="ui-spinner-core"></span>
      </span>
      <span v-if="label" class="ui-loader-label">{{ label }}</span>
    </div>

    <span class="visually-hidden">{{ label || "Chargement…" }}</span>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

/**
 * Reusable loader.
 *
 *   <AppLoader />                                  inline spinner (centered block)
 *   <AppLoader label="Chargement des colis…" />    spinner + text
 *   <AppLoader variant="skeleton" :rows="6" />     skeleton rows (lists / tables)
 *   <AppLoader mode="overlay" />                   covers the nearest position:relative parent
 *   <AppLoader mode="fullscreen" />                blocks the whole screen
 */
export default defineComponent({
  name: "AppLoader",
  props: {
    variant: {
      type: String as PropType<"spinner" | "skeleton">,
      default: "spinner",
    },
    mode: {
      type: String as PropType<"inline" | "overlay" | "fullscreen">,
      default: "inline",
    },
    size: {
      type: String as PropType<"sm" | "md">,
      default: "md",
    },
    label: {
      type: String,
      default: "",
    },
    rows: {
      type: Number,
      default: 5,
    },
  },
});
</script>

<style scoped>
.ui-loader-inline {
  display: grid;
  place-items: center;
  min-height: 180px;
  padding: var(--ui-space-6);
}
.ui-loader-inline.ui-loader-compact {
  min-height: 0;
  padding: var(--ui-space-3);
}
.ui-loader-overlay,
.ui-loader-fullscreen {
  display: grid;
  place-items: center;
  inset: 0;
  z-index: 50;
  background: color-mix(in srgb, var(--ui-surface) 78%, transparent);
  backdrop-filter: blur(3px);
  animation: ui-loader-fade var(--ui-dur) var(--ui-ease);
}
.ui-loader-overlay {
  position: absolute;
  border-radius: inherit;
}
.ui-loader-fullscreen {
  position: fixed;
  z-index: 1990;
}

/* ── Spinner ────────────────────────────────────────────────────── */
.ui-spinner-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ui-space-4);
}
.ui-spinner {
  position: relative;
  display: block;
  width: 56px;
  height: 56px;
}
.ui-loader-compact .ui-spinner {
  width: 28px;
  height: 28px;
}
.ui-spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(from 0deg, rgba(var(--ui-accent-rgb), 0) 0deg, rgba(var(--ui-accent-rgb), 0.15) 90deg, var(--ui-accent) 330deg, var(--ui-accent) 360deg);
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 4px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 4px));
  animation: ui-spin 0.9s linear infinite;
}
.ui-loader-compact .ui-spinner-ring {
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
}
.ui-spinner-core {
  position: absolute;
  inset: 32%;
  border-radius: 50%;
  background: var(--ui-accent);
  box-shadow: 0 0 0 6px var(--ui-accent-soft), 0 6px 16px rgba(var(--ui-accent-rgb), 0.35);
  animation: ui-pulse 1.4s var(--ui-ease) infinite;
}
.ui-loader-compact .ui-spinner-core {
  box-shadow: 0 0 0 3px var(--ui-accent-soft);
}
.ui-loader-label {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  letter-spacing: 0.02em;
}

/* ── Skeleton ───────────────────────────────────────────────────── */
.ui-skeleton {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.ui-skeleton-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 6px;
}
.ui-skeleton-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 4px;
  border-bottom: 1px solid var(--ui-border);
}
.ui-skeleton-row:last-child {
  border-bottom: 0;
}
.ui-skel {
  display: block;
  border-radius: var(--ui-radius-xs);
  background: linear-gradient(90deg, var(--ui-surface-sunken) 0%, var(--ui-surface-hover) 40%, var(--ui-surface-sunken) 80%);
  background-size: 300% 100%;
  animation: ui-shimmer 1.4s ease-in-out infinite;
}
.ui-skel-title { width: 180px; height: 18px; }
.ui-skel-chip { width: 110px; height: 34px; border-radius: var(--ui-radius-pill); }
.ui-skel-avatar { flex: 0 0 auto; width: 36px; height: 36px; border-radius: 50%; }
.ui-skel-line { height: 12px; }
.ui-skel-short { width: 14%; margin-left: auto; }
.ui-skel-pill { flex: 0 0 auto; width: 84px; height: 26px; border-radius: var(--ui-radius-pill); }

@keyframes ui-spin { to { transform: rotate(360deg); } }
@keyframes ui-pulse {
  0%, 100% { transform: scale(0.85); opacity: 0.85; }
  50% { transform: scale(1); opacity: 1; }
}
@keyframes ui-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
@keyframes ui-loader-fade { from { opacity: 0; } to { opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .ui-spinner-ring { animation-duration: 2.4s; }
  .ui-spinner-core,
  .ui-skel { animation: none; }
}
@media (max-width: 575.98px) {
  .ui-skel-short,
  .ui-skel-avatar { display: none; }
}
</style>
